/**
 * アコーディオンの初期化と制御を行うクラス
 * @example
 * // 初期化
 * const accordion = new AccordionController();
 * // すべて開く
 * accordion.openAll();
 * // すべて閉じる
 * accordion.closeAll();
 */
export class AccordionController {
    constructor() {
        this.accordions = [];
        this.init();

        // resizeイベントのリスナーを追加
        let resizeTimer;
        window.addEventListener("resize", () => {
            clearTimeout(resizeTimer);
            resizeTimer = window.setTimeout(() => {
                this.updateHeights();
            }, 250); // デバウンス処理
        });
    }

    /**
     * アコーディオンの高さを再計算
     * @private
     */
    updateHeights() {
        this.accordions.forEach((acc) => {
            // 現在のトランジション設定を保存
            const currentTransition = acc.element.style.transition;

            // トランジションを一時的に無効化
            acc.element.style.transition = "none";

            // 一時的に自動高さに設定
            acc.element.style.height = "auto";

            // 高さを取得
            const newHeight = acc.element.scrollHeight;

            // 新しい高さを保存
            acc.height = newHeight;

            // 状態に応じて高さを設定
            acc.element.style.height = acc.isOpen ? `${newHeight}px` : "0px";

            // トランジションを元に戻す
            // 強制的にリフロー（再描画）を発生させる
            acc.element.offsetHeight;
            acc.element.style.transition = currentTransition;
        });
    }

    init() {
        const accBtns = document.querySelectorAll(".acc__txt");

        accBtns.forEach((btn) => {
            if (window.innerWidth > 1024 && btn.classList.contains("sponly")) {
                return;
            }

            const ans = btn.previousElementSibling || btn.nextElementSibling;
            if (!ans) return;

            // 実際の高さを取得するために一時的に自動高さにする
            const currentTransition = ans.style.transition;
            ans.style.transition = "none";
            ans.style.height = "auto";
            const originalHeight = ans.scrollHeight;
            ans.style.height = "0";
            ans.offsetHeight;
            ans.style.transition = currentTransition;

            // アコーディオンの状態を保存
            this.accordions.push({
                element: ans,
                height: originalHeight,
                isOpen: false,
            });

            // クリックイベントの設定（heightパラメータを削除）
            btn.addEventListener("click", () => this.toggleAccordion(btn, ans));
        });
    }

    /**
     * 個別のアコーディオンの開閉
     * @private
     * @param {HTMLElement} btn - クリックされたボタン要素
     * @param {HTMLElement} ans - 開閉する内容要素
     */
    toggleAccordion(btn, ans) {
        // heightパラメータを削除
        const parent = btn.parentElement;
        if (!parent) return;

        const accState = this.accordions.find((acc) => acc.element === ans);
        if (!accState) return;

        // 開閉状態を切り替え
        const isOpen = parent.classList.toggle("is-on");
        accState.isOpen = isOpen;

        // 開く時は最新の高さを取得して使用
        if (isOpen) {
            // トランジションを一時的に無効化
            const currentTransition = ans.style.transition;
            ans.style.transition = "none";

            // 高さを再計算
            ans.style.height = "auto";
            const currentHeight = ans.scrollHeight;
            ans.style.height = "0";

            // 強制的にリフロー（再描画）を発生させる
            ans.offsetHeight;
            ans.style.transition = currentTransition;

            // 新しい高さを設定
            ans.style.height = `${currentHeight}px`;
            accState.height = currentHeight;
        } else {
            ans.style.height = "0px";
        }

        // scrollbackクラスの処理
        if (!isOpen && btn.classList.contains("scrollback")) {
            window.scrollTo({
                top: window.scrollY - accState.height,
                behavior: "smooth",
            });
        }
    }

    /**
     * すべてのアコーディオンを開く
     * @public
     */
    openAll() {
        this.accordions.forEach((acc) => {
            if (!acc.isOpen) {
                acc.element.style.height = `${acc.height}px`;
                acc.element.parentElement?.classList.add("is-on");
                acc.isOpen = true;
            }
        });
    }

    /**
     * すべてのアコーディオンを閉じる
     * @public
     */
    closeAll() {
        this.accordions.forEach((acc) => {
            if (acc.isOpen) {
                acc.element.style.height = "0px";
                acc.element.parentElement?.classList.remove("is-on");
                acc.isOpen = false;
            }
        });
    }
}

// 使用例
const accordion = new AccordionController();

// すべて開く場合
// accordion.openAll();

// すべて閉じる場合
// accordion.closeAll();
